import { api } from '../utils/api'; // Import the existing global API client
import API_ENDPOINTS from '../config/apiConfig'; // Import API_ENDPOINTS

// Fetches financial forecasts from SQL database
export const getFinancialForecasts = async ({ orgId, locationId, year }) => {
  try {
    const params = {};
    if (orgId) params.orgId = orgId;
    if (locationId) params.locationId = locationId;

    // Use SQL-based endpoint
    const response = await api.get(API_ENDPOINTS.FINANCIAL_FORECAST, { params });

    // Filter by year if provided (client-side filtering)
    let data = response.data;
    if (year && Array.isArray(data)) {
      data = data.filter(record => {
        const recordYear = new Date(record.forecastDate).getFullYear();
        return recordYear === year;
      });
    }

    return data;
  } catch (error) {
    console.error('Error fetching financial forecasts:', error);
    console.error('Error response:', error.response);
    console.error('Error config:', error.config);
    throw error;
  }
};

// Creates a single financial forecast entry in SQL database
// The backend handles delete-then-insert for upsert behavior
export const createFinancialForecast = async (forecastEntry) => {
  try {
    // SQL endpoint expects: orgId, locationId, chartOfAccountId, forecastDate,
    // driverType, driverInput, forecastAmount
    // Can also accept year/month instead of forecastDate
    const response = await api.post(API_ENDPOINTS.FINANCIAL_FORECAST, forecastEntry);
    return response.data;
  } catch (error) {
    console.error('Error creating financial forecast:', error);
    throw error;
  }
};

// Updates a single financial forecast entry in SQL database
export const updateFinancialForecast = async (forecastEntry) => {
  if (!forecastEntry.id) {
    throw new Error('Forecast entry ID is required for update.');
  }
  try {
    const response = await api.put(API_ENDPOINTS.FINANCIAL_FORECAST, forecastEntry);
    return response.data;
  } catch (error) {
    console.error('Error updating financial forecast:', error);
    throw error;
  }
};

// Creates/updates multiple forecast entries in bulk (Upsert behavior)
// SQL backend handles array of forecast entries with delete-then-insert for each
export const createBulkFinancialForecasts = async (forecastEntries) => {
  try {
    const response = await api.post(API_ENDPOINTS.FINANCIAL_FORECAST, forecastEntries);
    return response.data;
  } catch (error) {
    console.error('Error creating/updating bulk financial forecasts:', error);
    throw error;
  }
};

// Deletes a single financial forecast entry (soft delete)
export const deleteFinancialForecast = async (forecastId) => {
  if (!forecastId) {
    throw new Error('Forecast ID is required for deletion.');
  }
  try {
    const response = await api.delete(`${API_ENDPOINTS.FINANCIAL_FORECAST}?id=${forecastId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting financial forecast:', error);
    throw error;
  }
};

// Clears all driver inputs for a specific account (sets to 0)
export const clearForecastInputs = async ({ orgId, locationId, chartOfAccountId }) => {
  if (!orgId || !locationId || !chartOfAccountId) {
    throw new Error('orgId, locationId, and chartOfAccountId are required to clear inputs.');
  }
  try {
    const response = await api.patch(
      `${API_ENDPOINTS.FINANCIAL_FORECAST}?action=clearInputs&orgId=${orgId}&locationId=${locationId}&chartOfAccountId=${chartOfAccountId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error clearing forecast inputs:', error);
    throw error;
  }
};

// Clears all forecast entries for a specific account/month
// Used when saving a monthly static entry to replace any existing daily entries
export const clearForecastMonth = async ({ orgId, locationId, chartOfAccountId, year, month }) => {
  if (!orgId || !locationId || !chartOfAccountId || !year || !month) {
    throw new Error('orgId, locationId, chartOfAccountId, year, and month are required to clear month.');
  }
  try {
    const response = await api.patch(
      `${API_ENDPOINTS.FINANCIAL_FORECAST}?action=clearMonth&orgId=${orgId}&locationId=${locationId}&chartOfAccountId=${chartOfAccountId}&year=${year}&month=${month}`
    );
    return response.data;
  } catch (error) {
    console.error('Error clearing forecast month:', error);
    throw error;
  }
};

// NOTE: Version history is not currently supported in the SQL-based forecast system
// Version tracking will be available once the new `forecast` table (with version field) is implemented
// These stub functions are provided for backwards compatibility

// Stub: Returns empty array since version history is not supported in financialForecast table
export const getForecastVersionHistory = async ({ orgId, locationId, chartOfAccountId, year }) => {
  console.warn('Version history is not supported in the current SQL-based forecast system');
  return [];
};

// Stub: No-op since version history is not supported in financialForecast table
export const restoreForecastVersion = async (versionDoc) => {
  console.warn('Version restore is not supported in the current SQL-based forecast system');
  throw new Error('Version history is not currently supported. This feature will be available once the new forecast table is implemented.');
};

// Placeholder for a cache object, similar to budgetCache if needed
export const forecastCache = {
  data: {},
  invalidateCache: (orgId, locationId, year) => {
    forecastCache.data = {}; // Simple invalidation for now
  },
};

// You would typically have an apiClient instance configured here
// import axios from 'axios';
// const apiClient = axios.create({
//   baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
//   headers: {
//     'Content-Type': 'application/json',
//     // Authorization: `Bearer ${getToken()}` // If auth is needed
//   }
// }); 